import onEnvironmentChange from '@/observables/environment.observable'
import Environment from '@interfaces/environment.interface'
import { CampaignUserMedias } from '@interfaces/media.interface'
import { Video } from '@interfaces/video.interface'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import { AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'

export default class VisionService extends MainService {
  url: string
  logService = new LogService()

  constructor(visionUrl?: string) {
    super()
    if (!visionUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.vision
      })
    } else {
      this.url = visionUrl
    }
    this.logService.init('Vision Service')
  }

  async getMediaFeed(idProfile: string | number, page = 1, limit = 20, username, nextUrl) {
    const headers = await this.getHeaders()
    let params = `limit=${limit}&page=${page}`

    if (username) {
      params += `&username=${username}`
    }

    if (nextUrl) {
      params += `&nextUrl=${nextUrl}`
    }

    return this.http
      .get(`${this.url}/hub/user/${idProfile}/medias/feed?${params}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Can not get influencer feed from ' + idProfile,
          error,
        })
        return error.response
      })
  }

  async getCommentsFromMedia(
    idProfile: string | number,
    socialNetwork: 'instagram' | 'tiktok' | 'twitter' | 'youtube',
    idMedia: string,
    uid = false,
    idFacebook = false,
    nextUrl = ''
  ) {
    const headers = await this.getHeaders()
    return this.http
      .get(
        `${
          this.url
        }/hub/${socialNetwork}/midia/${idMedia}/comments?uid=${uid}&idFacebook=${idFacebook}&user=${idProfile}&nextUrl=${encodeURIComponent(
          nextUrl
        )}`,
        { headers }
      )
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Can not get comments from media' + idProfile,
          error,
        })
        return error.response
      })
  }

  async getCampaignUserMedias({
    socialId,
    campaignId,
    socialNetwork,
    perPage = 5,
    page = 0,
    sort_by = null,
    order_by = null,
    start_date = null,
    end_date = null,
    status = null,
    type = null,
    timezone = 'America/Sao_Paulo',
  }: CampaignUserMedias) {
    let prefix: string
    switch (socialNetwork) {
      case 'instagram':
        prefix = 'instagram|'
        break
      case 'twitter':
        prefix = 'twitter|'
        break
      default:
        prefix = ''
    }
    if (!socialId) throw new Error('Missing squidId to get medias')
    if (!campaignId) throw new Error('Missing id to get medias')

    const headers = await this.getHeaders()

    const paramsObject: CampaignUserMedias = {
      origin: socialNetwork,
      ids: campaignId,
      user_ids: `${prefix}${socialId}`,
      per_page: perPage,
      page,
      order_by,
      sort_by,
      start_date,
      end_date,
      timezone,
    }

    if (type) {
      paramsObject.type = type
    }

    const searchParams = new URLSearchParams(paramsObject as any)
    status?.forEach((s) => {
      searchParams?.append('status', s)
    })
    type?.forEach((s) => {
      searchParams?.append('type', s)
    })

    return await this.http
      .get(`${this.url}/hub/midias?${searchParams.toString()}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Can not get medias',
          error,
        })
        return error.response
      })
  }

  async postUploadMedia(
    campaignId: string,
    socialNetwork: 'instagram' | 'tiktok',
    mediaId?: string,
    isCollab?: boolean,
    username?: string,
    link?: string
  ) {
    if (!campaignId) throw new Error('Missing idCampaign')

    const headers = await this.getHeaders()
    let payload = {}
    let url = `${this.url}/hub/${socialNetwork}/media`

    if (socialNetwork === 'instagram') {
      url = `${this.url}/hub/instagram/campaign/${campaignId}/media/igtv`
      payload = {
        mediaId,
        isCollab,
        username,
      }
    } else {
      payload = {
        campaignId,
        link,
      }
    }

    return await this.http
      .post(url, payload, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on post ',
          error,
        })
        throw error.response
      })
  }

  async getYoutubeMidias(idSocialNetwork: string): Promise<Array<Video>> {
    const headers = await this.getHeaders()

    return await this.http
      .get(`${this.url}/hub/youtube/${idSocialNetwork}/midias`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Can not get medias',
          error,
        })
        throw error.response
      })
  }

  async postMediaYoutubeIntoCampaign(video: Video, idCampaign: string, referenceType = true, newMidia = true) {
    const headers = await this.getHeaders()
    return await this.http
      .post(
        `${this.url}/hub/midias/youtube/${video?.id}?reference_type=${referenceType}&new=${newMidia}`,
        {
          media: video,
          references: [idCampaign],
        },
        { headers }
      )
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Can not get medias',
          error,
        })
        throw error.response
      })
  }
}
